<template>
  <BaseTopBar
    v-if="isMounted"
    v-hotkey="shortcuts"
    :tint="tint"
    :is-navigation-displayed="isNavigationDisplayed"
    :left-panel-width="leftPanelWidth"
    is-hideable
    with-find
    @find="$emit('find')"
  >
    <template #left>
      <Breadcrumb
        v-if="!page.archived"
        :items="breadcrumbItems"
        :in-pages-view="inPagesView"
        :home="breadcrumbHome"
      />
    </template>
    <template #center>
      <div v-if="page.archived">
        <span>This page is archived.</span>
      </div>
    </template>
    <template #right>
      <!-- Pages view open action -->
      <BaseButton
        v-if="inPagesViewAction"
        kind="auxiliary"
        :text="inPagesViewAction.text"
        :tint="tint"
        @click="getActionFunc(inPagesViewAction, $event)"
      >
        <template #icon>
          <component :is="inPagesViewAction.icon" />
        </template>
      </BaseButton>
      <RecordingToolbar
        v-if="showRecordingToolbar"
        v-bind="recordingToolbarProps"
        @recording-control-click="onRecordingControlClick"
        @toggle-transcript="toggleTranscriptPopover"
        @close-transcript="closeRecordingTranscript"
      />
      <ShareButton
        :root="shareRoot"
        :organization-id="personalOrganizationId"
        :export-action="onExport"
        :tint="tint"
      />
      <!-- Main Button -->
      <BaseButton
        v-if="mainAction"
        kind="auxiliary"
        :text="mainAction.text"
        :tint="tint"
        @click="getActionFunc(mainAction, $event)"
      >
        <template #icon>
          <PinIcon :pinned="isPinned" />
        </template>
      </BaseButton>
    </template>
    <template #more>
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon>
          <component :is="action.icon" />
        </template>
        <template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { Page, type WorkspaceId } from "@ctrl/controller-rpc";

import { pageActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import { Tint } from "@/components/base/types";
import Breadcrumb, {
  type BreadcrumbHome,
  type BreadcrumbItem,
} from "@/components/Breadcrumb.vue";
import { PageContextActions } from "@/components/context-menu/context-actions";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import type { ExportRoot } from "@/components/marketplace/export";
import { createBuiltinExportAction } from "@/components/modals/export";
import { getPageShortcuts } from "@/components/object-shortcuts";
import RecordingToolbar from "@/components/RecordingToolbar.vue";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  selectAll as selectAllShortcut,
  Shortcuts,
} from "@/components/shortcuts";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import { useMeetingRecording } from "@/composables/recording/useMeetingRecording";
import { useRecordingToolbar } from "@/composables/recording/useRecordingToolbar";
import { useFolders, usePages } from "@/composables/usePages";
import { usePageHierarchy } from "@/page";
import type { MeetingRecordingContext } from "@/recording/types";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { parentIdOfRoutinePointer } from "@/utils/hierarchy";
import { makeMove } from "@/utils/move";
import { navigateBackward } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    Breadcrumb,
    BaseTopBar,
    PinIcon,
    RecordingToolbar,
    ShareButton,
  },
  props: {
    hasBlockActive: { type: Boolean, required: true },
    inPagesView: { type: Boolean, default: false },
    isNavigationDisplayed: { type: Boolean, default: true },
    leftPanelWidth: { type: Number, default: 0 },
    page: { type: Object as PropType<Page>, required: true },
    titleScrolledPast: { type: Boolean, default: false },
  },
  emits: {
    find: () => true,
    flush: () => true,
    selectAll: () => true,
  },
  async setup(props, { emit }) {
    const billing = inject.billing();
    const controller = inject.controller();
    const logger = inject.logger();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const simpleModal = inject.simpleModal();
    const profile = inject.profile();
    const router = inject.router();
    const menu = inject.menu();
    const palette = inject.palette();
    const automationHotkeys = inject.automationHotkeys();
    const currentWorkspaceId = inject.currentWorkspaceId();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);
    const flush = () => emit("flush");
    const selectAll = () => emit("selectAll");
    const { hasBlockActive, inPagesView, page, titleScrolledPast } =
      toRefs(props);
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const tint = computed<Tint>(() => (page.value.archived ? "red" : "none"));
    const releaser = useReleaser();

    // Automation hotkeys run on the object being viewed: arm them with this
    // page while mounted (same input as Mod+K → Run automation).
    const deactivateAutomationHotkeys = automationHotkeys.value?.activate(
      () => ({
        kind: "reference",
        referenceId: { kind: "page", id: page.value.id },
      })
    );
    if (deactivateAutomationHotkeys)
      releaser.defer(deactivateAutomationHotkeys);

    // Stop/transcript controls for a meeting recording targeting this page
    // (started via the notes editor's /meet command). Shown only while this
    // page owns the active recording — mirrors CustomObjectTopBar.
    const meetingEvent = computed<MeetingRecordingContext>(() => ({
      id: page.value.id,
      title: page.value.title.trim(),
      target: { kind: "page" },
    }));
    const audioRecording = releaser.add(useAudioRecording(logger));
    const meetingRecording = useMeetingRecording(
      logger,
      billing,
      audioRecording,
      meetingEvent
    );
    const showRecordingToolbar = computed(
      () => !!page.value.id && meetingRecording.isRecordingHere.value
    );
    const {
      release: releaseRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    } = useRecordingToolbar(meetingRecording, audioRecording, {
      toolbar: {
        compact: true,
        tint: "red",
        getTranscriptResetKey: () => page.value.id,
      },
    });
    releaser.defer(releaseRecordingToolbar);

    const { collapsedFolders, release: releaseFolders } = useFolders(profile);
    releaser.defer(releaseFolders);
    const { parentPointer, hierarchy } = await releaser.add(
      usePageHierarchy(controller, profile, page)
    );

    const breadcrumbItems = computed((): BreadcrumbItem[] => {
      const items = hierarchy.value ?? [];
      if (titleScrolledPast.value) {
        return [
          ...items,
          {
            title: page.value.title || "Untitled",
            link: { name: "page", params: { id: page.value.id } },
            fadeIn: true,
            onClick: () => {
              document
                .getElementById("scrollable-document")
                ?.scrollTo({ top: 0, behavior: "smooth" });
            },
          },
        ];
      }
      return items;
    });

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const workspacedPage: Page & { workspace?: WorkspaceId | null } =
        page.value;
      const ws = workspacedPage.workspace ?? currentWorkspaceId.value;
      return ws ? { type: "workspace", id: ws } : undefined;
    });
    const inPagesViewAction = computed<PageContextActions | null>(() =>
      inPagesView.value ? pageActions.open : null
    );
    // A computed is used here in order to allow setting it to undefined.
    //
    // We want to keep the mainAction variable in case we want to later
    // define a main action.
    const mainAction = computed<PageContextActions | undefined>(() => ({
      ...pageActions.togglePin,
      text: pinButtonText.value,
    }));
    const moreActions = computed<PageContextActions[]>(() => {
      return [
        pageActions.runAutomation,
        pageActions.copyLink,
        pageActions.duplicate,
        pageActions.move,
        {
          ...pageActions.delete,
          tint: "red" as const,
        },
      ];
    });
    const getActionFunc = (
      action: PageContextActions,
      mouseEvent?: MouseEvent
    ) =>
      action.action(
        { type: "page", value: page.value },
        {
          controller,
          logger,
          profile,
          router,
          simpleModal,
          menu,
          palette,
          move,
          flush,
          clickEvent: mouseEvent,
        },
        {
          beforeAction:
            //TODO: Build a better way to compare, tint screws up action === pageActions.delete
            action.text === pageActions.delete.text
              ? async () => {
                  await onDeleted();
                }
              : undefined,
        }
      );
    const {
      openPage,
      lastOpenPage,
      release: releasePages,
    } = usePages(controller, router, profile, logger);
    releaser.defer(releasePages);
    const onDeleted = async () => {
      // Drop the deleted page's collapse record, if any (folders are
      // expanded by default; only collapsed ones are recorded).
      if (collapsedFolders.value.includes(page.value.id))
        collapsedFolders.value = collapsedFolders.value.filter(
          (element: string) => element !== page.value.id
        );
      if (lastOpenPage.value == page.value.id) {
        lastOpenPage.value = null;
      }

      // In the pages view the hierarchy pane re-seats the selection itself
      // once the deletion ticks (the next page below, or the previous one —
      // see `reconcile` in `PersonalPagesSource`); a redirect here would
      // only fight it.
      if (router.currentRoute.value.name === RouteNames.OrgPages) return;

      // Runs before the deletion (the delete action's `beforeAction`), so
      // the parent pointer on the page's extension row is still readable:
      // redirect to the parent page when there is one, otherwise to the
      // pages root.
      const parent = parentPointer.value
        ? await parentIdOfRoutinePointer(controller, parentPointer.value)
        : null;
      const before = router.currentRoute.value.fullPath;
      await openPage(parent?.kind === "page" ? parent.id : null, true);
      if (router.currentRoute.value.fullPath === before) {
        await navigateBackward(router, menu);
      }
    };
    // Mod+F (find) is bound by BaseTopBar itself (`with-find`).
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return hasBlockActive.value
        ? {}
        : {
            ...getPageShortcuts(
              page.value,
              {
                ...injection,
                controller,
                logger,
                profile,
                router,
                simpleModal,
                menu,
                move,
                palette,
              },
              {
                flush,
                beforeDeleteAction: async () => {
                  await onDeleted();
                },
              }
            ),
            ...assignShortcuts(injection, selectAllShortcut, () => {
              selectAll();
            }),
          };
    });
    const isPinned = computed(() => menu.isItemPinned(page.value.id));
    const pinButtonText = computed(() => (isPinned.value ? "Unpin" : "Pin"));
    const togglePin = async () => {
      await menu.toggleObjectPin(page.value.id, "page");
    };

    const onExport = createBuiltinExportAction(controller, profile, () => ({
      title: page.value.title,
      notes: page.value.notes,
      extensionKind: "page",
      extensionRow: page.value.extension_row,
    }));

    const personalOrganizationId = computed(
      () => profile.value.user.personal.organizationId
    );
    // Marketplace packaging root.
    const shareRoot = computed<ExportRoot>(() => ({
      kind: "page",
      pageId: page.value.id,
      organizationId: personalOrganizationId.value,
    }));

    return {
      flush,
      getActionFunc,
      hierarchy,
      breadcrumbHome,
      breadcrumbItems,
      inPagesViewAction,
      isMounted,
      mainAction,
      moreActions,
      onDeleted,
      onExport,
      pageActions,
      personalOrganizationId,
      shareRoot,
      shortcuts,
      tint,
      menu,
      isPinned,
      togglePin,
      pinButtonText,
      showRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
</style>
